import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { Clock, Sparkles, X, ArrowRight } from "lucide-react";

const TRIAL_TOTAL_DAYS = 7;

function timeLeft(expiresAt) {
  if (!expiresAt) return null;
  const end = new Date(expiresAt).getTime();
  if (Number.isNaN(end)) return null;
  const ms = end - Date.now();
  if (ms <= 0) return { expired: true, days: 0, hours: 0, minutes: 0 };
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return { expired: false, days, hours, minutes, ms };
}

function label(t) {
  if (!t) return "";
  if (t.days >= 1) return `${t.days} ${t.days === 1 ? "day" : "days"} left`;
  if (t.hours >= 1) return `${t.hours}h ${t.minutes}m left`;
  return `${t.minutes} min left`;
}

export const TrialBanner = () => {
  const { user } = useAuth();
  const [, setTick] = useState(0);
  const [dismissedKey, setDismissedKey] = useState(() => localStorage.getItem("rcsa_trial_banner_dismissed") || "");

  // Tick every minute so the countdown stays live without a reload.
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const t = timeLeft(user?.subscription_expires_at);

  if (!user || user.subscription_status !== "trial" || !t || t.expired) return null;

  // Dismiss key is per-day so the banner returns each day of the trial.
  const todayKey = new Date().toISOString().slice(0, 10);
  const urgent = t.days <= 2;
  // Never let users dismiss the last-day nudge.
  if (dismissedKey === todayKey && !urgent) return null;

  const pctUsed = Math.min(100, Math.max(0, Math.round(((TRIAL_TOTAL_DAYS - (t.ms / 86400000)) / TRIAL_TOTAL_DAYS) * 100)));

  const dismiss = () => {
    localStorage.setItem("rcsa_trial_banner_dismissed", todayKey);
    setDismissedKey(todayKey);
  };

  return (
    <div
      data-testid="trial-banner"
      className={`relative overflow-hidden rounded-2xl border px-4 py-3 sm:px-5 sm:py-3.5 mb-6 flex items-center gap-4 shadow-soft ${
        urgent
          ? "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200"
          : "bg-gradient-to-r from-[#EFF6FF] to-[#F0FDFA] border-[#0B5FFF]/15"
      }`}
    >
      <div
        className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
          urgent ? "bg-amber-100 text-amber-700" : "bg-white text-[#0B5FFF] ring-1 ring-[#0B5FFF]/10"
        }`}
      >
        {urgent ? <Clock className="w-5 h-5" strokeWidth={1.75} /> : <Sparkles className="w-5 h-5" strokeWidth={1.75} />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            data-testid="trial-banner-countdown"
            className={`font-heading font-bold text-sm sm:text-base ${urgent ? "text-amber-900" : "text-[#111827]"}`}
          >
            {label(t)}
          </span>
          <span className="text-xs sm:text-sm text-slate-600">
            {urgent
              ? "in your free trial — upgrade to keep full access."
              : "in your 7-day Premium trial."}
          </span>
        </div>
        {/* progress */}
        <div className="mt-2 h-1.5 w-full max-w-md rounded-full bg-black/[0.06] overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${urgent ? "bg-amber-500" : "bg-[#0B5FFF]"}`}
            style={{ width: `${pctUsed}%` }}
          />
        </div>
      </div>

      <Link to="/pricing" data-testid="trial-banner-upgrade-btn" className="shrink-0">
        <button
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs sm:text-sm font-semibold text-white transition-colors ${
            urgent ? "bg-amber-600 hover:bg-amber-700" : "bg-[#0B5FFF] hover:bg-[#0A54E0]"
          }`}
        >
          Upgrade now <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </Link>

      {!urgent && (
        <button
          onClick={dismiss}
          data-testid="trial-banner-dismiss"
          aria-label="Dismiss"
          className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default TrialBanner;
